import { useCallback, useEffect, useRef, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Typography,
  useMediaQuery,
  useTheme
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import type * as React from "react";
import CologuardLogo from "assets/images/cologuard/cologuard-logo.svg";
import { COLORS } from "components/v3/Theme/colors";
import { HOW_IT_WORKS_STEPS } from "./constants";

const expandIcon = (
  <ExpandMoreIcon sx={{ color: COLORS.DARK_GRAY, fontSize: 28 }} />
);

const STEP_COUNT = HOW_IT_WORKS_STEPS.length;
const PIN_INSET_PX = 100;
const FOLLOW_SECTION_GAP_PX = 150;
const SCROLL_VH_PER_STEP = 70;
const PIN_SCROLL_VH = Math.max(0, STEP_COUNT - 1) * SCROLL_VH_PER_STEP;

const accordionSx = {
  background: "none",
  borderBottom: `1px solid ${COLORS.PRIMARY_LIGHTEST}`,
  borderRadius: "0 !important",
  boxShadow: "none",
  margin: 0,
  "&::before": {
    display: "none"
  },
  "&.Mui-expanded": {
    margin: 0
  },
  "&:last-of-type": {
    borderBottom: "none"
  }
} as const;

const accordionSummarySx = {
  cursor: "pointer",
  gap: 2,
  minHeight: 75,
  px: 0,
  py: 2,
  "& .MuiAccordionSummary-content": {
    alignItems: "center",
    margin: 0,
    marginRight: 2,
    pr: 1
  },
  "& .MuiAccordionSummary-expandIconWrapper": {
    color: COLORS.DARK_GRAY,
    marginLeft: 3
  }
} as const;

const accordionDetailsSx = {
  display: "flex",
  flexDirection: "column",
  gap: 2,
  pb: 2,
  pl: 0,
  pr: "35px",
  pt: 0
} as const;

const stepIndexFromPinProgress = (progress: number): number => {
  const clamped = Math.min(1, Math.max(0, progress));
  if (clamped >= 1) {
    return STEP_COUNT - 1;
  }

  return Math.min(STEP_COUNT - 1, Math.floor(clamped * STEP_COUNT));
};

const pinProgressForStep = (index: number): number =>
  (index + 0.5) / STEP_COUNT;

type HowItWorksSectionProps = {
  children?: React.ReactNode;
};

const HowItWorksSection: React.FC<HowItWorksSectionProps> = ({ children }) => {
  const theme = useTheme();
  const isDesktopLayout = useMediaQuery(theme.breakpoints.up("lg"), {
    noSsr: true
  });
  const isTallEnoughToPin = useMediaQuery("(min-height: 720px)", {
    noSsr: true
  });
  const prefersReducedMotion = useMediaQuery(
    "(prefers-reduced-motion: reduce)",
    { noSsr: true }
  );
  const shouldPin =
    isDesktopLayout && isTallEnoughToPin && !prefersReducedMotion;

  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const stepTriggerRefs = useRef<(HTMLDivElement | null)[]>([]);
  const pinRootRef = useRef<HTMLDivElement | null>(null);
  const stickyPanelRef = useRef<HTMLDivElement | null>(null);
  const ignorePinScrollRef = useRef(false);
  const ignorePinScrollTimeoutRef = useRef<number | undefined>(undefined);

  const setStepRef = useCallback(
    (index: number) => (element: HTMLDivElement | null) => {
      stepTriggerRefs.current[index] = element;
    },
    []
  );

  useEffect(() => {
    let ticking = false;

    const updateFromPinProgress = (): void => {
      if (ignorePinScrollRef.current) {
        return;
      }

      const root = pinRootRef.current;
      const stickyPanel = stickyPanelRef.current;
      if (!root || !stickyPanel) {
        return;
      }

      const pinDistance = root.offsetHeight - stickyPanel.offsetHeight;
      if (pinDistance <= 0) {
        setActiveStepIndex(0);
        return;
      }

      const nextIndex = stepIndexFromPinProgress(
        -root.getBoundingClientRect().top / pinDistance
      );
      setActiveStepIndex((previous) =>
        previous === nextIndex ? previous : nextIndex
      );
    };

    const updateFromTargetLine = (): void => {
      const root = pinRootRef.current;
      if (!root) {
        return;
      }

      const sectionRect = root.getBoundingClientRect();
      if (sectionRect.bottom < 0 || sectionRect.top > window.innerHeight) {
        return;
      }

      const targetLine = window.innerHeight * 0.4;
      let bestIndex = 0;
      let bestDistance = Number.POSITIVE_INFINITY;

      stepTriggerRefs.current.forEach((element, index) => {
        if (!element) {
          return;
        }

        const summary = element.querySelector<HTMLElement>(
          ".MuiAccordionSummary-root"
        );
        const rect = (summary ?? element).getBoundingClientRect();
        const center = rect.top + rect.height / 2;
        const distance = Math.abs(center - targetLine);

        if (distance < bestDistance) {
          bestDistance = distance;
          bestIndex = index;
        }
      });

      setActiveStepIndex((previous) =>
        previous === bestIndex ? previous : bestIndex
      );
    };

    const updateActiveStepFromScroll = (): void => {
      ticking = false;

      if (shouldPin) {
        updateFromPinProgress();
        return;
      }

      updateFromTargetLine();
    };

    const onScroll = (): void => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(updateActiveStepFromScroll);
      }
    };

    const releasePinScrollLock = (): void => {
      ignorePinScrollRef.current = false;
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    window.addEventListener("scrollend", releasePinScrollLock);
    updateActiveStepFromScroll();

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      window.removeEventListener("scrollend", releasePinScrollLock);
      window.clearTimeout(ignorePinScrollTimeoutRef.current);
    };
  }, [shouldPin]);

  const activeStep = HOW_IT_WORKS_STEPS[activeStepIndex];

  const scrollToPinnedStep = useCallback(
    (index: number): void => {
      const root = pinRootRef.current;
      const stickyPanel = stickyPanelRef.current;
      if (!root || !stickyPanel) {
        return;
      }

      const pinDistance = root.offsetHeight - stickyPanel.offsetHeight;
      if (pinDistance <= 0) {
        return;
      }

      const rootTop = window.scrollY + root.getBoundingClientRect().top;
      const targetTop = rootTop + pinProgressForStep(index) * pinDistance;

      ignorePinScrollRef.current = true;
      window.clearTimeout(ignorePinScrollTimeoutRef.current);
      ignorePinScrollTimeoutRef.current = window.setTimeout(() => {
        ignorePinScrollRef.current = false;
      }, 800);

      window.scrollTo({
        top: targetTop,
        behavior: prefersReducedMotion ? "auto" : "smooth"
      });
    },
    [prefersReducedMotion]
  );

  const handleAccordionChange = useCallback(
    (index: number) =>
      (_event: React.SyntheticEvent, isExpanded: boolean): void => {
        if (!isExpanded) {
          return;
        }

        setActiveStepIndex(index);

        if (shouldPin) {
          scrollToPinnedStep(index);
          return;
        }

        const summary = stepTriggerRefs.current[
          index
        ]?.querySelector<HTMLElement>(".MuiAccordionSummary-root");
        summary?.scrollIntoView({
          block: "center",
          behavior: prefersReducedMotion ? "auto" : "smooth"
        });
      },
    [prefersReducedMotion, scrollToPinnedStep, shouldPin]
  );

  const sectionHeader = (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Typography component="p" variant="body_strong_xl_black" sx={{ m: 0 }}>
        How Cologuard works
      </Typography>
      <Typography component="p" variant="body_black" sx={{ m: 0 }}>
        Genomic Life has partnered with Exact Sciences, the maker of Cologuard,
        to provide members with a simple way to get colon cancer screening.
      </Typography>
    </Box>
  );

  return (
    <Box
      component="section"
      ref={pinRootRef}
      sx={{
        flexShrink: 0,
        position: "relative",
        width: "100%"
      }}
    >
      <Box
        ref={stickyPanelRef}
        sx={{
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          gap: shouldPin ? `${FOLLOW_SECTION_GAP_PX}px` : { md: 8, xs: 6 },
          position: shouldPin ? "sticky" : "static",
          pt: shouldPin ? `${PIN_INSET_PX}px` : 0,
          top: 0,
          width: "100%"
        }}
      >
        <Box sx={{ display: { lg: "none", xs: "flex" } }}>{sectionHeader}</Box>

        <Box
          sx={{
            alignItems: { lg: "center", xs: "stretch" },
            display: "flex",
            flexDirection: { lg: "row", xs: "column" },
            gap: { lg: 5, xs: 4 }
          }}
        >
          <Box
            sx={{
              alignItems: "center",
              display: { lg: "flex", xs: "none" },
              flex: "0 0 42%",
              justifyContent: "center",
              maxWidth: { lg: 420, xs: "100%" },
              width: "100%"
            }}
          >
            <Box
              alt=""
              component="img"
              key={activeStep?.key ?? "fallback"}
              src={activeStep?.image ?? CologuardLogo}
              sx={{
                display: "block",
                height: "auto",
                maxWidth: "100%",
                objectFit: "contain",
                width: "100%"
              }}
            />
          </Box>

          <Box
            sx={{
              display: "flex",
              flex: 1,
              flexDirection: "column",
              minWidth: 0
            }}
          >
            <Box sx={{ display: { lg: "flex", xs: "none" }, mb: 4 }}>
              {sectionHeader}
            </Box>

            {HOW_IT_WORKS_STEPS.map((step, index) => (
              <Accordion
                key={step.key}
                ref={setStepRef(index)}
                disableGutters
                expanded={activeStepIndex === index}
                onChange={handleAccordionChange(index)}
                sx={accordionSx}
              >
                <AccordionSummary
                  expandIcon={expandIcon}
                  sx={accordionSummarySx}
                >
                  <Typography component="span" variant="body_strong_black">
                    {step.title}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={accordionDetailsSx}>
                  <Typography
                    component="div"
                    variant="body_black"
                    sx={{ m: 0, whiteSpace: "pre-line" }}
                  >
                    {step.body}
                  </Typography>
                  <Box
                    alt=""
                    component="img"
                    src={step.image ?? CologuardLogo}
                    sx={{
                      display: { lg: "none", xs: "block" },
                      height: "auto",
                      maxWidth: "100%",
                      mt: 1,
                      objectFit: "contain"
                    }}
                  />
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </Box>
        {children}
      </Box>
      {shouldPin && <Box aria-hidden sx={{ height: `${PIN_SCROLL_VH}dvh` }} />}
    </Box>
  );
};

export default HowItWorksSection;
